/**
 * The app is being served from an address that cannot hold a session.
 *
 * **Found the hard way on 2026-09-27**, over about two hours. Somebody was
 * signing in at `http://app.dev.myrave.io:5173` — Vite's own port, plain HTTP —
 * while the API is `https://api.dev.myrave.io:8443` behind
 * `scripts/dev-tls.mjs`. Every sign-in SUCCEEDED: the server wrote a session and
 * set the cookie. Not one of them survived to the next request.
 *
 * ## Why, exactly
 *
 * Chrome's **schemeful same-site** rule counts the scheme as part of the site.
 * `http://app.dev.myrave.io` and `https://api.dev.myrave.io` therefore differ by
 * SITE, not merely by origin — and a `SameSite=Lax` cookie is never sent on a
 * cross-site subresource request. So the boot refresh posts to the API with no
 * cookie at all and is told 401, which is the correct answer to a request that
 * carried no credential.
 *
 * ## Why it took two hours
 *
 * Every symptom pointed somewhere else. The account was fine, the server
 * accepted every attempt, the cookie header was well-formed, CORS allowed
 * credentials, the certificate verified, and the whole flow worked in the same
 * Chrome version — because the person testing it was on `:8443` and never once
 * checked which address the person reporting it was on. Incognito failed too,
 * which killed the extension and stale-state theories and should have been the
 * clue that the URL itself was the variable.
 *
 * **A dev server reachable two ways, where one silently cannot hold a session,
 * is a trap.** This is the sign that says so, rather than a 401 that reads as a
 * rejected password.
 */
import { apiUrl } from '@/lib/tenant';
import { useT } from '@/i18n';

/**
 * Can this page's origin carry a session to the API?
 *
 * The only break worth naming is an HTTP page against an HTTPS API — the
 * schemeful-same-site case above. http→http (plain `localhost` development) and
 * https→https are both fine, and an https page against an http API is already
 * blocked by the browser as mixed content, loudly, in the console.
 */
export function originCannotHoldSession(
  location: { protocol: string } = window.location,
  api: string = apiUrl(),
): boolean {
  return location.protocol === 'http:' && api.startsWith('https://');
}

/** The same path, on the address that works. */
export function correctOrigin(
  location: { hostname: string; pathname: string; search: string } = window.location,
  api: string = apiUrl(),
): string {
  const port = new URL(api).port;
  return `https://${location.hostname}${port ? `:${port}` : ''}${location.pathname}${location.search}`;
}

export function WrongOrigin() {
  const tr = useT();
  const target = correctOrigin();

  return (
    <main className="app-wrong-origin">
      <div className="app-wrong-origin-body">
        <h1 className="app-h1">
          {tr('This address cannot keep you signed in')}
        </h1>

        <p className="app-p">
          {tr('The app is being served over http and the API is on https. A browser treats those as different sites, so the session cookie is never sent — you can sign in, and the next request will not know you.')}
        </p>

        <p className="app-p">
          {tr('Nothing is wrong with your account. Use the address below.')}
        </p>

        {/*
          * A link, not a redirect. An automatic one would bounce somebody
          * between two ports if the TLS proxy is not running, and this screen
          * exists precisely because a silent failure wasted hours.
          */}
        <a href={target} dir="ltr" className="app-wrong-origin-link">
          {target}
        </a>

        <p className="app-p app-fine">
          {tr('Started by {command} together with the app — see {script}.', { command: 'pnpm dev', script: 'scripts/dev-tls.mjs' })}
        </p>
      </div>
    </main>
  );
}
